/**
 * Shared filename sanitizer for plan keys — frontend mirror.
 *
 * MUST stay byte-identical to the backend original at:
 *   portal-api/src/utils/plan-filename.ts
 *
 * The "Opgeslagen" badge in InstallateurTaakTab compares the sanitized SP
 * filename against the server-written S3 key suffix — if the two copies drift,
 * files will appear unsaved immediately after being saved.
 */
const MAX_PLAN_FILENAME = 80;

function cleanPlanFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9À-ÿ._\-\s]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+/, '')
    .replace(/\s+/g, '-');
}

export function sanitizePlanFilename(name: string): string {
  const cleaned = cleanPlanFilename(name);
  if (cleaned.length <= MAX_PLAN_FILENAME) return cleaned || 'bestand';
  // Too long: shorten the part before the extension, so a long SharePoint
  // name still ends in `.pdf` and opens after download.
  const ext = cleaned.match(/\.[a-zA-Z0-9]{1,10}$/)?.[0] ?? '';
  const base = cleaned.slice(0, cleaned.length - ext.length);
  return base.slice(0, MAX_PLAN_FILENAME - ext.length) + ext;
}

/**
 * The name `sanitizePlanFilename` gave before it kept the extension (a plain
 * 80-char cut). Plans saved then still carry it in their key, so the
 * "Opgeslagen" badge matches it too.
 */
export function legacyPlanFilename(name: string): string {
  return cleanPlanFilename(name).slice(0, MAX_PLAN_FILENAME) || 'bestand';
}
